import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowLeft, TrendingUp } from 'lucide-react';

import { trendsApi, type TrendRow } from './api';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';

/**
 * Caso 12 parte 2 — Compare event metrics within a project or brand.
 *
 * Routes:
 *   /projects/:id/trends           → project scope (eventGroupId)
 *   /brand/:id/trends              → brand scope (companyId)
 *   /trends?projectId=X|brandId=Y  → query-param escape hatch for deep
 *                                     linking from external dashboards.
 *
 * v1 renders a grouped bar chart (responses, leads, prizes delivered)
 * plus a table for exact numbers. A line chart variant lives in v2 when
 * we add date-range filtering.
 */
export function EventComparisonPage() {
  const t = useT();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const [search] = useSearchParams();
  useFavoritable(t('trends.title'));

  // Scope resolution: prefer URL params, then query string.
  const projectId =
    Number(params.id) ||
    Number(search.get('projectId')) ||
    0;
  const brandId = Number(search.get('brandId')) || 0;
  const scope: 'project' | 'brand' =
    brandId && !params.id ? 'brand' : 'project';

  const { data, isLoading } = useQuery({
    queryKey: ['trends', scope, scope === 'project' ? projectId : brandId],
    queryFn: () =>
      scope === 'project'
        ? trendsApi.byProject(projectId)
        : trendsApi.byBrand(brandId),
    enabled:
      (scope === 'project' && projectId > 0) ||
      (scope === 'brand' && brandId > 0),
  });

  // Recharts needs short labels — truncate to keep the X axis legible. The
  // tooltip still shows the full name.
  const chartData = useMemo(() => {
    return (data ?? []).map((row) => ({
      ...row,
      shortName:
        row.eventName.length > 14
          ? row.eventName.slice(0, 13) + '…'
          : row.eventName,
    }));
  }, [data]);

  return (
    <div className="page space-y-5">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
      >
        <ArrowLeft className="h-4 w-4" /> {t('common.back')}
      </button>

      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--color-fg-primary)]">
          <TrendingUp className="h-5 w-5" /> {t('trends.title')}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('trends.subtitle')}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t('trends.chart.title')}</CardTitle>
          <CardDescription>{t('trends.chart.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-64" />
          ) : (data ?? []).length === 0 ? (
            <p className="py-8 text-center text-sm text-[var(--color-fg-muted)]">
              {t('trends.empty')}
            </p>
          ) : (
            <div className="h-72 w-full">
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
                  <XAxis dataKey="shortName" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--color-bg-surface)',
                      border: '1px solid var(--color-border-default)',
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.eventName ?? ''
                    }
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    dataKey="responsesCount"
                    name={t('trends.metric.responses')}
                    fill="var(--color-accent)"
                  />
                  <Bar
                    dataKey="leadsCount"
                    name={t('trends.metric.leads')}
                    fill="var(--color-info)"
                  />
                  <Bar
                    dataKey="prizesDelivered"
                    name={t('trends.metric.prizes')}
                    fill="var(--color-success)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('trends.table.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40" />
          ) : (data ?? []).length === 0 ? (
            <p className="text-sm text-[var(--color-fg-muted)]">
              {t('trends.empty')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border-subtle)] text-left text-xs uppercase text-[var(--color-fg-muted)]">
                    <th className="py-2 pr-3">{t('trends.table.event')}</th>
                    <th className="py-2 pr-3">{t('trends.table.date')}</th>
                    <th className="py-2 pr-3 text-right">{t('trends.metric.responses')}</th>
                    <th className="py-2 pr-3 text-right">{t('trends.metric.leads')}</th>
                    <th className="py-2 pr-3 text-right">{t('trends.metric.prizesDistributed')}</th>
                    <th className="py-2 pr-3 text-right">{t('trends.metric.prizes')}</th>
                    <th className="py-2">{t('trends.table.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data ?? []).map((row: TrendRow) => (
                    <tr
                      key={row.eventId}
                      className="border-b border-[var(--color-border-subtle)] hover:bg-[var(--color-bg-elevated)]"
                    >
                      <td className="py-2 pr-3 font-medium">{row.eventName}</td>
                      <td className="py-2 pr-3 text-xs text-[var(--color-fg-muted)]">
                        {row.startDate
                          ? new Date(row.startDate).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {row.responsesCount}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {row.leadsCount}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {row.prizesDistributed}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {row.prizesDelivered}
                      </td>
                      <td className="py-2">
                        <Badge tone="neutral">
                          {row.eventStatus ?? '—'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
